/**
 * Parámetros legales de Colombia, versionados por fecha.
 *
 * ── POR QUÉ ESTO ES UNA TABLA Y NO CONSTANTES ───────────────────────────────
 *
 * La UVT cambia cada año. La tasa de usura cambia CADA MES. El salario mínimo
 * cambia cada año. El precio de la gasolina cambia cuando el gobierno quiera.
 *
 * Si se escriben como constantes en el código, pasan dos cosas malas:
 *   1. Al actualizarlas, los cálculos del año pasado quedan mal. Un movimiento
 *      de enero de 2026 debe calcularse con la UVT de 2026, no con la de 2027.
 *   2. Nadie se acuerda de actualizarlas, y la app miente en silencio.
 *
 * Por eso cada valor tiene su vigencia y se consulta POR FECHA.
 *
 * ⚠️ Todo valor lleva su fuente. Si no está confirmado, se dice.
 */

/**
 * Unidad de Valor Tributario. Es la unidad con la que la DIAN expresa topes
 * y multas, para no tener que reescribir las leyes cada año por la inflación.
 */
export const UVT = [
  { desde: '2026-01-01', valor: 52374, fuente: 'Resolución DIAN 000238 de 2025', confirmado: true },
];

/**
 * Tasa de usura: el interés máximo legal. Cobrar por encima es delito.
 * La publica la Superintendencia Financiera y cambia TODOS LOS MESES.
 */
export const USURA = [
  {
    desde: '2026-09-01',
    ea: 29.24,        // efectivo anual, en %
    mv: 2.16,         // mes vencido, en %
    fuente: 'Resolución 1260 de 2026, Superintendencia Financiera',
    confirmado: true,
  },
];

/** Salario mínimo mensual legal vigente. */
export const SMMLV = [
  { desde: '2026-01-01', valor: 1750905, confirmado: true },
];

/** Gravamen a los Movimientos Financieros: el famoso 4x1000. */
export const GMF = {
  tarifa: 0.004,      // 4 por cada 1.000 = 0,4%

  /**
   * Tope mensual exento para la cuenta que marcas como exenta.
   * Numeral 1 del artículo 879 del Estatuto Tributario.
   */
  tope_exento_uvt: 350,

  /**
   * Los depósitos de bajo monto (Nequi y Daviplata en su versión básica)
   * van por otro numeral, el 25, y tienen un tope MUCHO menor.
   * Confundirlos hace que la app te diga que estás exento cuando no lo estás.
   */
  tope_bajo_monto_uvt: 65,

  confirmado: true,
  fuente: 'Estatuto Tributario art. 879, numerales 1 y 25',
};

/**
 * Aportes a seguridad social de un contratista por prestación de servicios.
 *
 * Esto le aplica a Esteban por el contrato con la Asamblea de Córdoba.
 * Un contratista NO tiene empleador que le aporte: paga él, completo.
 *
 * La base (IBC) es el 40% del valor mensualizado del contrato, con piso de
 * 1 salario mínimo y techo de 25.
 *
 * ⚠️ La ARL depende del nivel de riesgo. 0,522% es el nivel I (trabajo de
 *    oficina). Si el contrato implica riesgo mayor, sube. CONFIRMAR.
 */
export const SEGURIDAD_SOCIAL_OPS = {
  porcentaje_ibc: 0.40,
  salud: 0.125,
  pension: 0.16,
  arl_nivel_1: 0.00522,
  confirmado: true,
  fuente: 'Ley 1955 de 2019 art. 244; Decreto 1273 de 2018',
};

/**
 * Lo que un contratista NO recibe y por lo tanto debe apartar él mismo.
 * Un empleado de nómina recibe prima y cesantías; un contratista, no.
 */
export const APARTAR_OPS = {
  prima_equivalente: 0.0833,      // un mes de sueldo al año, prorrateado
  cesantias_equivalente: 0.0833,
  vacaciones_equivalente: 0.0417, // 15 días hábiles al año
};

/**
 * Precio de la gasolina, para calcular el costo de un viaje.
 * ⚠️ NO CONFIRMADO para Montería. El promedio nacional se tomó de prensa,
 *    no de una fuente oficial. La app deja editarlo, que es lo honesto.
 */
export const GASOLINA = [
  {
    desde: '2026-09-01',
    corriente_galon: 15848,
    ciudad: 'promedio nacional',
    confirmado: false,
    nota: 'Precio de Montería sin confirmar. Ajústalo en Configuración.',
  },
];

/** Peajes conocidos, para el presupuesto de eventos. */
export const PEAJES = [
  {
    ruta: 'Montería – Sincelejo',
    km: 121.6,
    categoria_i: 4400,
    confirmado: true,
  },
];

// ─── Consultas ──────────────────────────────────────────────────────────────

/** Devuelve el parámetro vigente en una fecha dada ('YYYY-MM-DD'). */
export function vigente(tabla, fecha) {
  const f = fecha || new Date().toISOString().slice(0, 10);
  const candidatos = tabla
    .filter((p) => p.desde <= f)
    .sort((a, b) => b.desde.localeCompare(a.desde));
  return candidatos[0] ?? null;
}

export function uvt(fecha) {
  return vigente(UVT, fecha)?.valor ?? null;
}

/**
 * Tope mensual en pesos que puedes mover SIN pagar 4x1000.
 *
 * @param {'normal'|'bajo_monto'} clase
 *   'normal'     → cuenta de ahorros o corriente marcada como exenta.
 *   'bajo_monto' → Nequi o Daviplata básicos. Tope mucho menor.
 */
export function topeExentoGmf(clase = 'normal', fecha) {
  const v = uvt(fecha);
  if (!v) return null;
  const topeUvt = clase === 'bajo_monto'
    ? GMF.tope_bajo_monto_uvt
    : GMF.tope_exento_uvt;
  return Math.round(v * topeUvt);
}

/**
 * Convierte una tasa efectiva anual a mes vencido.
 *
 *   i_MV = (1 + i_EA)^(1/12) − 1
 *
 * NO es dividir entre 12. Ese error subestima el costo de una deuda, y en una
 * tarjeta de crédito la diferencia se nota. Verificado contra el tarifario
 * oficial de Nu.
 */
export function eaAMv(ea) {
  return ((1 + ea / 100) ** (1 / 12) - 1) * 100;
}

/** El camino de vuelta: de mes vencido a efectivo anual. */
export function mvAEa(mv) {
  return ((1 + mv / 100) ** 12 - 1) * 100;
}

/** ¿Esta tasa es legal? Por encima de la usura es delito. */
export function superaUsura(tasaEa, fecha) {
  const u = vigente(USURA, fecha);
  if (!u) return null;
  return tasaEa > u.ea;
}

/**
 * Lo que se paga en la planilla (PILA) sobre un IBC.
 *
 * La planilla no cobra el porcentaje pelado: cada aporte se sube al múltiplo
 * de $100 de arriba. Lo prueban al peso las dos planillas de agosto de
 * Esteban: con IBC de $1.225.634 (21 días sobre el mínimo) pagó $196.200 de
 * pensión, $153.300 de salud y $6.400 de ARL = $355.900; y con la corrección
 * a $1.320.060, $211.300 + $165.100 + $6.900 = $383.300. Sin el redondeo la
 * app decía $508.148 por un mes entero; la planilla cobra $508.300.
 */
export function cotizacionesPila(ibc) {
  const s = SEGURIDAD_SOCIAL_OPS;
  // El margen evita que un 196.200,0000001 de la aritmética de punto
  // flotante suba a 196.300.
  const alCienDeArriba = (x) => Math.ceil((x - 1e-6) / 100) * 100;
  const base = Math.ceil(ibc);
  const salud = alCienDeArriba(base * s.salud);
  const pension = alCienDeArriba(base * s.pension);
  const arl = alCienDeArriba(base * s.arl_nivel_1);
  return { ibc: base, salud, pension, arl, total: salud + pension + arl };
}

/**
 * Cuánto debe apartar Esteban de un pago del contrato.
 *
 * Devuelve el desglose para que la app pueda decir, cuando entra la plata:
 * "de estos $4.000.000, aparta $464.000 para seguridad social. Te quedan
 * $3.536.000 de verdad."
 *
 * Que es información que cambia decisiones, no un dato decorativo.
 */
export function aportesOps(valorMensualContrato, fecha) {
  const s = SEGURIDAD_SOCIAL_OPS;
  const minimo = vigente(SMMLV, fecha)?.valor ?? 0;

  let ibc = Math.ceil(valorMensualContrato * s.porcentaje_ibc);
  let ajustadoAlPiso = false;

  // Piso: aunque el 40% dé menos, se cotiza sobre un salario mínimo.
  // Por eso a los contratos pequeños la seguridad social les pesa más.
  if (ibc < minimo) {
    ibc = minimo;
    ajustadoAlPiso = true;
  }
  const techo = minimo * 25;
  if (ibc > techo) ibc = techo;

  const { salud, pension, arl, total } = cotizacionesPila(ibc);

  return {
    ibc,
    ajustadoAlPiso,
    salud,
    pension,
    arl,
    total,
    neto: valorMensualContrato - total,
    porcentajeDelContrato: valorMensualContrato
      ? (total / valorMensualContrato) * 100
      : 0,
  };
}

// ─── Festivos colombianos y días hábiles ────────────────────────────────────

/**
 * Calcula el Domingo de Pascua con el algoritmo de Meeus/Jones/Butcher.
 * De ahí salen Jueves Santo, Viernes Santo, Ascensión, Corpus Christi y
 * Sagrado Corazón, que cambian de fecha cada año.
 */
function domingoDePascua(anio) {
  const a = anio % 19;
  const b = Math.floor(anio / 100);
  const c = anio % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(anio, mes - 1, dia));
}

/** Corre una fecha al lunes siguiente, como manda la Ley Emiliani. */
function alLunes(fecha) {
  const d = new Date(fecha);
  const diaSemana = d.getUTCDay();          // 0 = domingo, 1 = lunes
  if (diaSemana === 1) return d;
  const sumar = diaSemana === 0 ? 1 : 8 - diaSemana;
  d.setUTCDate(d.getUTCDate() + sumar);
  return d;
}

const iso = (d) => d.toISOString().slice(0, 10);

/**
 * Los festivos de Colombia para un año.
 *
 * Se calculan, no se escriben a mano: una lista fija de 2026 estaría mal en
 * 2027 y la app diría fechas de pago equivocadas sin avisar.
 *
 * Tres clases:
 *   · Fijos, que caen donde caigan.
 *   · Movibles por la Ley Emiliani, que se corren al lunes siguiente.
 *   · Los de Semana Santa, que dependen de la Pascua.
 */
export function festivosColombia(anio) {
  const fijos = [[0, 1], [4, 1], [6, 20], [7, 7], [11, 8], [11, 25]];
  const emiliani = [[0, 6], [2, 19], [5, 29], [7, 15], [9, 12], [10, 1], [10, 11]];

  const dias = fijos.map(([m, d]) => iso(new Date(Date.UTC(anio, m, d))));
  for (const [m, d] of emiliani) {
    dias.push(iso(alLunes(new Date(Date.UTC(anio, m, d)))));
  }

  const pascua = domingoDePascua(anio);
  const desdePascua = (n) => {
    const d = new Date(pascua);
    d.setUTCDate(d.getUTCDate() + n);
    return d;
  };

  // Jueves y Viernes Santo NO se mueven: caen antes de la Pascua.
  dias.push(iso(desdePascua(-3)));   // Jueves Santo
  dias.push(iso(desdePascua(-2)));   // Viernes Santo
  // Estos tres sí se corren al lunes.
  dias.push(iso(alLunes(desdePascua(43))));   // Ascensión
  dias.push(iso(alLunes(desdePascua(64))));   // Corpus Christi
  dias.push(iso(alLunes(desdePascua(71))));   // Sagrado Corazón

  return [...new Set(dias)].sort();
}

/** El n-ésimo día hábil de un mes: ni sábados, ni domingos, ni festivos. */
export function diaHabil(anio, mes, n) {
  const festivos = new Set(festivosColombia(anio));
  let contados = 0;
  for (let dia = 1; dia <= 31; dia++) {
    const f = new Date(Date.UTC(anio, mes - 1, dia));
    if (f.getUTCMonth() !== mes - 1) break;          // se pasó de mes
    const diaSemana = f.getUTCDay();
    if (diaSemana === 0 || diaSemana === 6) continue; // fin de semana
    if (festivos.has(iso(f))) continue;
    contados++;
    if (contados === n) return iso(f);
  }
  return null;
}

/**
 * Qué día hábil te toca pagar la seguridad social, según tu cédula.
 *
 * Fuente: calendario unificado de aportes (mismo para empleadores y para
 * trabajadores independientes). Los dos últimos dígitos de la cédula, sin
 * dígito de verificación, mandan.
 */
export const CALENDARIO_PILA = [
  { hasta: 7, diaHabil: 2 },   { hasta: 14, diaHabil: 3 },
  { hasta: 21, diaHabil: 4 },  { hasta: 28, diaHabil: 5 },
  { hasta: 35, diaHabil: 6 },  { hasta: 42, diaHabil: 7 },
  { hasta: 49, diaHabil: 8 },  { hasta: 56, diaHabil: 9 },
  { hasta: 63, diaHabil: 10 }, { hasta: 69, diaHabil: 11 },
  { hasta: 75, diaHabil: 12 }, { hasta: 81, diaHabil: 13 },
  { hasta: 87, diaHabil: 14 }, { hasta: 93, diaHabil: 15 },
  { hasta: 99, diaHabil: 16 },
];

/**
 * Hasta cuándo puedes pagar la seguridad social de un mes.
 *
 * ⚠️ La seguridad social se paga MES VENCIDO: lo de septiembre se paga en
 * octubre. Confundir esto es la forma más común de caer en mora.
 *
 * @param {string} cedula   Tu número de cédula. Solo se miran los dos últimos.
 * @param {string} periodo  El mes que estás pagando, 'YYYY-MM'.
 */
export function fechaLimitePila(cedula, periodo) {
  const digitos = String(cedula || '').replace(/\D/g, '');
  if (digitos.length < 2) return null;
  const dosUltimos = Number(digitos.slice(-2));

  const fila = CALENDARIO_PILA.find((f) => dosUltimos <= f.hasta);
  if (!fila) return null;

  // El pago va en el mes SIGUIENTE al periodo cotizado.
  const [a, m] = periodo.split('-').map(Number);
  const anioPago = m === 12 ? a + 1 : a;
  const mesPago = m === 12 ? 1 : m + 1;

  return {
    fecha: diaHabil(anioPago, mesPago, fila.diaHabil),
    diaHabil: fila.diaHabil,
    periodo,
    nota: `Los aportes de ${periodo} se pagan en ${anioPago}-${String(mesPago).padStart(2, '0')}.`,
  };
}

/**
 * Cuántos días de financiación te da una compra, según cuándo la hagas.
 *
 * Es la diferencia entre comprar el 14 y comprar el 16 con corte el 15:
 * la misma compra, casi un mes más de plazo. La app lo muestra EN VIVO
 * mientras registras, que es cuando sirve.
 */
export function diasDeFinanciacion(fechaCompra, diaCorte, diaLimitePago) {
  const f = new Date(fechaCompra + 'T00:00:00');
  const dia = f.getDate();

  // Si compras después del corte, entras en el periodo siguiente.
  const mesesAdelante = dia > diaCorte ? 1 : 0;
  const corte = new Date(f.getFullYear(), f.getMonth() + mesesAdelante, diaCorte);

  // La fecha límite cae después del corte; si el día es menor, es del mes que sigue.
  const limite = new Date(
    corte.getFullYear(),
    corte.getMonth() + (diaLimitePago <= diaCorte ? 1 : 0),
    diaLimitePago
  );

  return Math.round((limite - f) / 86400000);
}
